import ErrorIcon from "@mui/icons-material/Error";
import InfoIcon from "@mui/icons-material/Info";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PersonIcon from "@mui/icons-material/Person";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import { Avatar, Box, Chip, IconButton, Menu, MenuItem, Paper, Typography } from "@mui/material";
import { useState } from "react";
import React from "react";

import { CHAT_BADGE } from "../../../common/colors";
import { FeatureFlag } from "../../../common/posthog";
import CopyButton from "../../../components/CopyButton";
import MarkdownRenderer from "../../../components/MarkdownRenderer";
import { ArtifactType } from "../../../types/artifact";
import { formatDate } from "../../../utils/date";
import { AnswerMode, ChatMessage as IChatMessage } from "../stores/chatStore";
import { ARTIFACT_TYPE_LABELS } from "../stores/chatTypes";
import ChunkConfirmationContent from "./ChunkConfirmationContent";
import ContextSummary from "./ContextSummary";

interface ChatMessageProps {
  message: IChatMessage;
  onEdit?: (message: IChatMessage) => void;
}

interface MessageStyle {
  bgcolor: string;
  color: string;
  icon: React.ReactElement;
  avatarColor: string;
}

const TEMPERATURE_FRACTION_DIGITS = 2;
const JSON_STRINGIFY_INDENT = 2;
const EMPTY_CHUNKS = 0;

const WAITING_MESSAGES = [
  "Cogitating...",
  "Ruminating...",
  "Digesting...",
  "Reflecting...",
  "Unwrapping...",
  "Unfolding...",
  "Interpolating...",
  "Extrapolating..."
];

const ANSWER_MODE_LABELS: Record<Exclude<AnswerMode, "none">, string> = {
  quick: "Explore",
  deep: "Deeper Look"
};

const DEFAULT_MESSAGE_STYLE: MessageStyle = {
  bgcolor: "#f9fafb",
  color: "#101828",
  icon: <InfoIcon />,
  avatarColor: "#6b7280"
};

const THINKING_STYLE: MessageStyle = {
  bgcolor: "#f5f5f5",
  color: "#666666",
  icon: <SmartToyIcon />,
  avatarColor: "#9c27b0"
};

const ROLE_STYLES: Record<string, MessageStyle> = {
  user: { bgcolor: "#f5f8ff", color: "#101828", icon: <PersonIcon />, avatarColor: "#4f46e5" },
  assistant: { bgcolor: "#ffffff", color: "#101828", icon: <SmartToyIcon />, avatarColor: "#10b981" },
  error: { bgcolor: "#fff1f2", color: "#be123c", icon: <ErrorIcon />, avatarColor: "#ef4444" }
};

const SYSTEM_STYLE_NORMAL: MessageStyle = {
  bgcolor: "#f0f9ff", color: "#0c5c9e", icon: <InfoIcon />, avatarColor: "#3b82f6"
};

const SYSTEM_STYLE_EXCEEDS: MessageStyle = {
  bgcolor: "#fff1f2", color: "#be123c", icon: <InfoIcon />, avatarColor: "#ef4444"
};

const ROLE_NAMES: Record<string, string> = {
  user: "You",
  assistant: "Enlighten",
  system: "System",
  error: "Error"
};

const badgeStyles = {
  container: { display: "flex", gap: 0.5, mt: 1, flexWrap: "wrap" },
  datasetChip: {
    bgcolor: CHAT_BADGE.dataset.background,
    color: CHAT_BADGE.dataset.text,
    fontSize: "0.7rem",
    height: 20
  },
  modeChip: {
    bgcolor: CHAT_BADGE.answerMode.background,
    color: CHAT_BADGE.answerMode.text,
    fontSize: "0.7rem",
    height: 20
  }
} as const;

interface UserMessageBadgesProps {
  types?: ArtifactType[];
  answerMode?: AnswerMode;
}

const UserMessageBadges = ({ types, answerMode }: UserMessageBadgesProps): React.ReactElement | null => {
  if (!types?.length && !answerMode) {
    return null;
  }

  return (
    <Box sx={badgeStyles.container}>
      {types?.map(type => (
        <Chip
          key={type}
          label={ARTIFACT_TYPE_LABELS[type] ?? type}
          size="small"
          sx={badgeStyles.datasetChip}
        />
      ))}
      {answerMode && answerMode !== "none" && (
        <Chip
          label={ANSWER_MODE_LABELS[answerMode]}
          size="small"
          sx={badgeStyles.modeChip}
        />
      )}
    </Box>
  );
};

const getSystemStyle = (message: IChatMessage): MessageStyle =>
  message.exceedsTokenLimit ? SYSTEM_STYLE_EXCEEDS : SYSTEM_STYLE_NORMAL;

const getMessageStyle = (message: IChatMessage): MessageStyle => {
  if (message.isThinking) {
    return THINKING_STYLE;
  }
  if (message.role === "system") {
    return getSystemStyle(message);
  }
  return ROLE_STYLES[message.role] ?? DEFAULT_MESSAGE_STYLE;
};

const getRandomWaitingMessage = (): string =>
  WAITING_MESSAGES[Math.floor(Math.random() * WAITING_MESSAGES.length)];

const getRoleName = (role: string): string => ROLE_NAMES[role] ?? role;

const MessageAvatar = ({ style }: { style: MessageStyle }): React.ReactElement => (
  <Avatar
    sx={{
      bgcolor: style.avatarColor,
      width: 28,
      height: 28,
      mr: 1,
      mt: 0.5
    }}
  >
    {style.icon}
  </Avatar>
);

const MessageMeta = ({ message }: { message: IChatMessage }): React.ReactElement => {
  const formattedTime = formatDate(message.timestamp, "h:mm a");

  return (
    <>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {getRoleName(message.role)}
      </Typography>
      <Typography variant="caption" sx={{ ml: 1, mt: 0.2, color: "text.secondary" }}>
        {formattedTime}
      </Typography>
      {message.temperature !== undefined && (
        <Typography variant="caption" sx={{ ml: 1, color: "text.secondary" }}>
          (temp: {message.temperature.toFixed(TEMPERATURE_FRACTION_DIGITS)})
        </Typography>
      )}
    </>
  );
};

const AssistantCopyAction = ({ content }: { content: string }): React.ReactElement => (
  <CopyButton
    text={content}
    tooltipTitle="Copy response"
    size="small"
    richText={true}
  />
);

const SystemCopyAction = ({ contextData }: { contextData: unknown }): React.ReactElement => (
  <FeatureFlag flag="copy-datasets" fallback={<></>}>
    <CopyButton
      text={contextData ? JSON.stringify(contextData, null, JSON_STRINGIFY_INDENT) : ""}
      tooltipTitle="Copy context to clipboard"
      size="small"
      richText={false}
    />
  </FeatureFlag>
);

interface UserMenuProps {
  message: IChatMessage;
  onEdit: (message: IChatMessage) => void;
}

const UserMenu = ({ message, onEdit }: UserMenuProps): React.ReactElement => {
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(menuAnchorEl);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>): void => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = (): void => {
    setMenuAnchorEl(null);
  };

  const handleEdit = (): void => {
    onEdit(message);
    handleMenuClose();
  };

  return (
    <>
      <IconButton
        aria-label="more"
        id={`message-menu-${message.id}`}
        aria-controls={open ? `message-menu-${message.id}` : undefined}
        aria-expanded={open ? "true" : undefined}
        aria-haspopup="true"
        onClick={handleMenuClick}
        size="small"
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        id={`message-menu-${message.id}`}
        anchorEl={menuAnchorEl}
        open={open}
        onClose={handleMenuClose}
        MenuListProps={{
          ["aria-labelledby"]: `message-menu-${message.id}`
        }}
      >
        <MenuItem onClick={handleEdit}>Edit & Re-run</MenuItem>
      </Menu>
    </>
  );
};

interface MessageActionsProps {
  message: IChatMessage;
  onEdit?: (message: IChatMessage) => void;
}

const MessageActions = ({ message, onEdit }: MessageActionsProps): React.ReactElement | null => {
  if (message.role === "assistant" && message.content) {
    return <AssistantCopyAction content={message.content} />;
  }
  if (message.role === "system" && message.contextFilter) {
    return <SystemCopyAction contextData={message.contextData} />;
  }
  if (message.role === "user" && onEdit) {
    return <UserMenu message={message} onEdit={onEdit} />;
  }
  return null;
};

const MessageHeader = ({ message, onEdit }: MessageActionsProps): React.ReactElement => {
  const style = getMessageStyle(message);

  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", mb: 0.5, justifyContent: "space-between" }}>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <MessageAvatar style={style} />
        <MessageMeta message={message} />
      </Box>

      <Box sx={{ display: "flex", alignItems: "center" }}>
        <MessageActions message={message} onEdit={onEdit} />
      </Box>
    </Box>
  );
};

const ThinkingContent = ({ message }: { message: IChatMessage }): React.ReactElement => (
  <Box>
    <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1 }}>
      {message.isStreaming ? "Thinking in progress..." : "Thinking process completed"}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        fontFamily: "monospace",
        whiteSpace: "pre-wrap",
        fontSize: "0.8rem",
        color: "#555"
      }}
    >
      {message.thinkingContent
        || (message.isStreaming ? getRandomWaitingMessage() : "No thinking process captured")}
    </Typography>
  </Box>
);

const AssistantContent = ({ message }: { message: IChatMessage }): React.ReactElement => {
  if (message.isThinking) {
    return <ThinkingContent message={message} />;
  }

  return (
    <MarkdownRenderer
      content={
        message.content
        || (message.isStreaming ? getRandomWaitingMessage() : "No response received")
      }
    />
  );
};

const DefaultContent = ({ message }: { message: IChatMessage }): React.ReactElement => (
  <>
    <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
      {message.content}
    </Typography>
    {message.role === "user" && (
      <UserMessageBadges types={message.includedArtifactTypes} answerMode={message.answerMode} />
    )}
  </>
);

const MessageContent = ({ message }: { message: IChatMessage }): React.ReactElement => {
  if (message.role === "assistant") {
    return <AssistantContent message={message} />;
  }

  if (message.role === "system" && message.chunks && message.chunks.length > EMPTY_CHUNKS) {
    return <ChunkConfirmationContent message={message} />;
  }

  if (message.role === "system" && message.contextFilter) {
    return (
      <ContextSummary
        content={message.content}
        contextFilter={message.contextFilter}
      />
    );
  }

  return <DefaultContent message={message} />;
};

const STREAMING_AFTER_SX = {
  ["&::after"]: {
    content: "\"\"",
    position: "absolute",
    bottom: 0,
    left: 0,
    height: "2px",
    width: "100%",
    background: "linear-gradient(90deg, #4f46e5 30%, rgba(255, 255, 255, 0) 60%)",
    backgroundSize: "200% 100%",
    animation: "loading 1.5s infinite"
  }
};

const MessageBody = ({ message }: { message: IChatMessage }): React.ReactElement => {
  const style = getMessageStyle(message);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        ml: 4.5,
        bgcolor: style.bgcolor,
        color: style.color,
        borderRadius: 2,
        position: "relative",
        ...(message.isStreaming && STREAMING_AFTER_SX)
      }}
    >
      <style>
        {`
        @keyframes loading {
          0% { background-position: 200% 0; }
          100% { background-position: 0 0; }
        }
        `}
      </style>
      <MessageContent message={message} />
    </Paper>
  );
};

const ChatMessage = ({ message, onEdit }: ChatMessageProps): React.ReactElement => (
  <Box sx={{ mb: 2 }}>
    <MessageHeader message={message} onEdit={onEdit} />
    <MessageBody message={message} />
  </Box>
);

export default ChatMessage;
